import { Component, ElementRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import get from 'lodash-es/get';

import { OneIdService } from '@finder/shared/services/one-id/one-id.service';
import { LocalStorageService } from '@finder/shared/services/local-storage/local-storage.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import {
    EmailCaptureStatus,
    Guest,
    OneIdUIResult,
    OneIdNewsletter,
    OneIdResponse,
    EmailCaptureStyle
} from '@finder/shared/services/one-id/one-id.interface';
import { ONE_ID_UI_ANIMATIONS } from '@finder/shared/components/one-id-ui/one-id-ui.animations';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { FocusInputComponent } from '../focus-input/focus-input.component';
import { FontIconComponent } from '../font-icon/font-icon.component';

// eslint-disable-next-line max-len
const EMAIL_PATTERN = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
const SIGNUP_SUCCESS = 'SUCCESS';
const OPT_IN = 'opt-in';
const MOUSEDOWN = 'mousedown';
const logName = `OneID UI`;

@Component({
    selector: 'finder-one-id-ui',
    templateUrl: './one-id-ui.component.html',
    styleUrls: ['./one-id-ui.component.scss'],
    animations: [...ONE_ID_UI_ANIMATIONS],
    standalone: true,
    imports: [
        CommonModule,
        ButtonComponent,
        FocusInputComponent,
        FontIconComponent,
    ],
})
export class OneIdUIComponent implements OnInit, OnDestroy {
    @Input() data: any;
    @Input() captureStyle: EmailCaptureStyle;
    @Output() oidResult = new EventEmitter<OneIdUIResult>();

    guest: Guest;
    isProcessing = false;
    errorMessage = '';
    legalMessage = '';
    isEmailFocused = false;
    label: string;
    isOneIdEnabled = false;

    email = new FormControl<string>('', [
        Validators.required,
        Validators.pattern(EMAIL_PATTERN)
    ]);

    readonly EmailCaptureStyle = EmailCaptureStyle;

    private oneIdService: OneIdService;
    private clickListenerBind = this.onClick.bind(this);
    private destroy$ = new Subject<void>();
    private cookieKey: string;
    private invalidEmail: string;

    constructor(
        private elementRef: ElementRef,
        private oneIdServiceResolver: OneIdService,
        private logger: LoggerService,
        private localStorage: LocalStorageService,
        private windowRef: WindowRef,
        private route: ActivatedRoute,
        private debugLog: DebugLogService,
    ) {}

    async ngOnInit() {
        // The service is a promise (via app provider `useFactory`) so we inject the resolver
        // into the contructor _then_ wait until the promise resolves the actual service.
        // We cannot ignore the catch, so just ignore this whole bit - we need to mock it anyway
        // and without this nothing else works, so who cares about the "coverage"?
        /* istanbul ignore next */
        try {
            this.oneIdService = await this.oneIdServiceResolver;
        } catch {
            this.data = false;
            this.oidResult.emit({ hasError: true });
            this.logger.error(`OneID was not able to initialize. The newsletter component can not be displayed.`);

            return;
        }

        // Get resources
        this.invalidEmail = get(this.data, 'errors.invalidEmail');
        this.label = get(this.data, 'placeholder');
        this.legalMessage = get(this.data, 'toc');

        // Set default theme
        this.data.buttonTheme = this.data.buttonTheme || 'reversed';

        // Once the dynamically added script is loaded make a call to get the newsletters
        this.debugLog.log('***** OneID service loaded successfully!');
        this.initializeOneId();

        // Set cookie key
        const urlFriendly = this.route.snapshot.params.id;
        const context = get(this.data, 'options.context', urlFriendly);
        this.cookieKey = this.oneIdService.getCookieKey(context);
    }

    /* istanbul ignore next */
    ngOnDestroy() {
        // Tidy up observables
        this.destroy$.next();
        this.destroy$.complete();

        // Just to make sure we cleanup
        this.windowRef.nativeWindow.removeEventListener(
            MOUSEDOWN,
            this.clickListenerBind,
            { capture: true }
        );

        this.oneIdService.off(OPT_IN);
    }

    signUp(event) {
        // Handle DOM stuff
        this.signUpDom(event);

        // Either get the email from the guest info or from the input
        this.isProcessing = true;
        from(this.oneIdService.launchNewsletters(this.data.campaignId, this.data.options))
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isProcessing = false),
                /* istanbul ignore next */
                catchError((error) => {
                    this.logger.error(`OneID error: ${error}`);
                    this.oidResult.emit({ result: error.message, hasError: false });

                    return of(false);
                })
            )
            .subscribe((response: any) => {
                // When the pop up is created response doesn't exist
                if (!response) {
                    return;
                }

                // There is an `error_codes` value that comes back that we should maybe look at?
                // But during dev this was failing CORS and _still_ coming back 'SUCCESS' so not
                // sure what would have to happen to get an error?!?
                if (response.data === SIGNUP_SUCCESS) {
                    this.onSubscribe();
                }
            });
    }

    onTermsFocusOut(event: FocusEvent): void {
        const relatedTarget = event.relatedTarget as HTMLElement;
        /* istanbul ignore if */
        if (!relatedTarget) {
            return;
        }

        this.hideTerms(relatedTarget);
    }

    onClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        this.hideTerms(target);
    }

    onFocused(event) {
        if (event) {
            this.onFocus();
        } else {
            this.onEmailFocusOut();
        }
    }

    private initializeOneId() {
        /**
         * First, get the guest (if applicable). Second, get the newsletters. We want to do both of these
         * calls because we use the 2 lists for cross-referencing. Based on the guests nesletter options we
         * can determine if the guest is subscribed and then setup the UI accordingly - if subbed, show a
         * message, if not, show the button to sub.
         */
        this.getGuest().subscribe((guestData: OneIdResponse<Guest>) => {
            // This value can be `undefined` - we don't get a guest when they are not logged in.
            this.guest = get(guestData, 'data');
            // At this point OneId should be ready to use
            this.isOneIdEnabled = true;

            // Check for subscribed status
            const isSubscribed = this.guest ?
                this.guest.marketing?.some((campaign) => campaign.code === this.data.campaignId && campaign.subscribed) :
                this.localStorage.get(this.cookieKey);

            const currentStatus = isSubscribed ? EmailCaptureStatus.SUBSCRIBED_GUEST : EmailCaptureStatus.UNSUBSCRIBED_GUEST;
            this.oidResult.emit({ status: currentStatus, hasError: false });
        });

        this.oneIdService.on(OPT_IN, this.onSubscribe.bind(this));
    }

    private onFocus() {
        // Reset vars
        this.isEmailFocused = true;
        this.errorMessage = '';

        // Add event listener so we can capture mousedown and determine if it is in/out
        // of focus target zone
        this.windowRef.nativeWindow.addEventListener(
            MOUSEDOWN,
            this.clickListenerBind,
            { capture: true }
        );

        /* istanbul ignore if */
        if (this.email.value) {
            return;
        }

        this.email.reset('');
    }

    private onEmailFocusOut() {
        if (!this.email.value) {
            this.errorMessage = '';
            this.email.reset('');

            return;
        }

        // Determine input state so we know if we need to display the error message
        this.errorMessage = this.email.dirty && this.email.invalid
            ? this.invalidEmail
            : '';
    }

    private hideTerms(target: HTMLElement) {
        // If the target element is within the "focus zone" we don't want to hide the terms
        const emailSignup = this.elementRef.nativeElement.querySelector('.email-signup');
        if (emailSignup && emailSignup.contains(target)) {
            return;
        }

        // Remove the mousedown focus target listener
        this.windowRef.nativeWindow.removeEventListener(
            MOUSEDOWN,
            this.clickListenerBind,
            { capture: true }
        );

        this.isEmailFocused = false;
    }

    private getGuest(): Observable<OneIdResponse<Guest>> {
        return from(this.oneIdService.getGuest())
            .pipe(
                takeUntil(this.destroy$),
                catchError((err) =>{
                    this.debugLog.error(`***** ${logName} guest error`, err);

                    // We may not get a guest, so no need to worry
                    // If the guest is not logged in, for example, it throws an error
                    // This will catch that and return `undefined`
                    return of(void 0);
                })
            );
    }

    private getNewsletters(): Observable<OneIdResponse<OneIdNewsletter>> {
        this.debugLog.log(`***** ${logName} camapign ID`, this.data.campaignId);

        return from(this.oneIdService.getInlineNewsletters(this.data.campaignId))
            .pipe(
                takeUntil(this.destroy$),
            );
    }

    private onSubscribe(): void {
        /* istanbul ignore else */
        if (!this.guest) {
            this.localStorage.set(this.cookieKey , true);
        }

        this.oidResult.emit({
            result: true,
            status: EmailCaptureStatus.SUBSCRIBED_GUEST,
            hasError: false
        });
    }

    /* istanbul ignore next */
    private signUpDom(event) {
        // If we aren't using the inline form we can skip
        if (this.captureStyle === EmailCaptureStyle.MODAL) {
            return;
        }

        // Show error message if applicable
        if (!this.email.valid && !this.guest) {
            this.errorMessage = this.invalidEmail;

            return;
        } else if (this.email.valid && !this.guest) {
            // Handle DOM stuff
            const emailSignup = event.target.closest('div.email-signup');
            if (!emailSignup) {
                return;
            }

            const input = emailSignup.querySelector('input');
            const button = emailSignup.querySelector('button');

            input.setAttribute('disabled', 'disabled');
            button.setAttribute('disabled', 'disabled');
        }
    }
}
